import { useMemo, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { SlidersHorizontal, ChevronRight, MessageCircle, PackageOpen } from "lucide-react";

import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { WhatsAppFloat } from "../components/WhatsAppFloat";
import { FilterPanel } from "../components/vanities/FilterPanel";
import { CategoryPills } from "../components/vanities/CategoryPills";
import { SortSelect } from "../components/vanities/SortSelect";
import { ActiveFilterChips } from "../components/vanities/ActiveFilterChips";
import { ProductCard } from "../components/vanities/ProductCard";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "../components/ui/sheet";

import {
  EMPTY_FILTERS,
  filterParams,
  totalActive,
} from "../lib/filters";
import { useProducts } from "../lib/useApi";
import { WHATSAPP_URL } from "../lib/site";

const initFromParams = (params) => {
  const base = { ...EMPTY_FILTERS };
  const mount = params.get("mount");
  if (mount === "wall") base.mounting = ["Wall Mounted"];
  if (mount === "floor") base.mounting = ["Floor Mounted"];
  const style = params.get("style");
  if (style) base.style = [style];
  return base;
};

export default function Vanities() {
  const [params] = useSearchParams();
  const [filters, setFilters] = useState(() => initFromParams(params));
  const [sort, setSort] = useState("recommended");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const serverParams = useMemo(
    () => ({ ...filterParams(filters), sort, kind: "vanity" }),
    [filters, sort]
  );

  const { data, isLoading, isError, refetch } = useProducts(serverParams);
  const results = data?.items ?? [];
  const facets = data?.facets;

  const addons = useProducts({ kind: "addon" });
  const addonItems = addons.data?.items ?? [];
  const gridItems = [...results, ...addonItems];

  const toggle = useCallback((groupId, value) => {
    setFilters((prev) => {
      const cur = prev[groupId];
      const next = cur.includes(value)
        ? cur.filter((v) => v !== value)
        : [...cur, value];
      return { ...prev, [groupId]: next };
    });
  }, []);

  const remove = useCallback((groupId, value) => {
    setFilters((prev) => ({
      ...prev,
      [groupId]: prev[groupId].filter((v) => v !== value),
    }));
  }, []);

  const clearAll = useCallback(() => setFilters({ ...EMPTY_FILTERS }), []);

  const selectPill = useCallback((id) => {
    setFilters((prev) => ({
      ...prev,
      mounting: id === "all" ? [] : [id],
    }));
  }, []);

  const activeCount = totalActive(filters);

  return (
    <div data-testid="vanities-page">
      <Header />

      <main className="pt-28 md:pt-32 pb-24 bg-ivory min-h-screen">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          {/* Breadcrumb */}
          <nav
            data-testid="breadcrumb"
            className="flex items-center gap-2 text-xs text-taupe mb-8"
            aria-label="Breadcrumb"
          >
            <Link to="/" className="hover:text-charcoal transition-colors">Home</Link>
            <ChevronRight size={12} />
            <span>Bathroom Vanities</span>
            <ChevronRight size={12} />
            <span className="text-charcoal">All Vanities</span>
          </nav>

          {/* Category header */}
          <header className="max-w-3xl mb-10">
            <h1 className="font-serif font-light text-charcoal text-4xl md:text-6xl leading-[1.02] tracking-tight">
              Bathroom Vanities
            </h1>
            <p className="mt-5 text-charcoal/70 leading-relaxed">
              A focused collection of wall mounted and floor mounted vanities —
              curated for fit, finish and everyday practicality. Narrow by the
              details that matter, and find yours with confidence.
            </p>
            <p
              data-testid="result-count"
              className="mt-6 eyebrow text-taupe"
            >
              {isLoading
                ? "Loading vanities…"
                : `Showing ${results.length} ${results.length === 1 ? "vanity" : "vanities"}`}
            </p>
          </header>

          {/* Category pills */}
          <div className="mb-8">
            <CategoryPills mounting={filters.mounting} onSelect={selectPill} />
          </div>

          {/* Layout: sidebar + grid */}
          <div className="lg:grid lg:grid-cols-[260px_1fr] lg:gap-12">
            {/* Desktop sidebar */}
            <aside
              data-testid="filter-sidebar"
              className="hidden lg:block sticky top-28 self-start max-h-[calc(100vh-8rem)] overflow-y-auto pr-2 no-scrollbar"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif text-2xl text-charcoal">Filters</h2>
                {activeCount > 0 && (
                  <button
                    onClick={clearAll}
                    data-testid="sidebar-clear"
                    className="text-xs text-taupe link-sweep"
                  >
                    Clear ({activeCount})
                  </button>
                )}
              </div>
              <FilterPanel facets={facets} filters={filters} onToggle={toggle} />
            </aside>

            {/* Right column */}
            <section>
              {/* Sort + mobile controls */}
              <div className="flex items-center justify-between gap-3 mb-6">
                {/* Mobile filter trigger */}
                <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
                  <SheetTrigger asChild>
                    <button
                      data-testid="mobile-filter-trigger"
                      className="lg:hidden inline-flex items-center gap-2 rounded-full border border-beige px-5 py-2.5 text-sm font-medium text-charcoal"
                    >
                      <SlidersHorizontal size={15} strokeWidth={1.6} />
                      Filters
                      {activeCount > 0 && (
                        <span className="grid place-items-center h-5 min-w-5 px-1 rounded-full bg-charcoal text-ivory text-[0.65rem]">
                          {activeCount}
                        </span>
                      )}
                    </button>
                  </SheetTrigger>
                  <SheetContent
                    side="left"
                    data-testid="mobile-filter-drawer"
                    className="w-full sm:max-w-md bg-ivory border-beige overflow-y-auto p-0"
                  >
                    <div className="flex items-center justify-between px-6 py-5 border-b border-beige sticky top-0 bg-ivory z-10">
                      <h2 className="font-serif text-2xl text-charcoal">Filters</h2>
                      {activeCount > 0 && (
                        <button
                          onClick={clearAll}
                          data-testid="drawer-clear"
                          className="text-xs text-taupe link-sweep mr-8"
                        >
                          Clear all
                        </button>
                      )}
                    </div>
                    <div className="px-6 py-7">
                      <FilterPanel facets={facets} filters={filters} onToggle={toggle} />
                    </div>
                    <div className="sticky bottom-0 bg-ivory border-t border-beige px-6 py-4">
                      <button
                        onClick={() => setDrawerOpen(false)}
                        data-testid="drawer-view-results"
                        className="w-full rounded-full bg-charcoal text-ivory py-3.5 text-sm font-medium"
                      >
                        View {results.length} results
                      </button>
                    </div>
                  </SheetContent>
                </Sheet>

                <div className="ml-auto">
                  <SortSelect value={sort} onChange={setSort} />
                </div>
              </div>

              {/* Active chips */}
              {activeCount > 0 && (
                <div className="mb-8">
                  <ActiveFilterChips filters={filters} onRemove={remove} onClear={clearAll} />
                </div>
              )}

              {/* Grid / empty state */}
              {isError ? (
                <div
                  data-testid="catalog-error"
                  className="border border-dashed border-beige rounded-sm py-20 px-6 text-center flex flex-col items-center"
                >
                  <PackageOpen size={40} strokeWidth={1} className="text-taupe" />
                  <h3 className="font-serif text-3xl text-charcoal mt-6">
                    Couldn't load vanities
                  </h3>
                  <p className="mt-3 max-w-md text-charcoal/65 text-sm leading-relaxed">
                    The catalog is temporarily unavailable. Please try again.
                  </p>
                  <button
                    onClick={() => refetch()}
                    data-testid="catalog-retry"
                    className="mt-8 rounded-full bg-charcoal text-ivory px-7 py-3.5 text-sm font-medium"
                  >
                    Try again
                  </button>
                </div>
              ) : results.length > 0 ? (
                <motion.div
                  layout
                  data-testid="product-grid"
                  className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-12"
                >
                  <AnimatePresence mode="popLayout">
                    {gridItems.map((p, i) => (
                      <ProductCard key={p.id} product={p} index={i} autoplay />
                    ))}
                  </AnimatePresence>
                </motion.div>
              ) : (
                <>
                  <div
                    data-testid="empty-state"
                    className="border border-dashed border-beige rounded-sm py-20 px-6 text-center flex flex-col items-center"
                  >
                    <PackageOpen size={40} strokeWidth={1} className="text-taupe" />
                    <h3 className="font-serif text-3xl text-charcoal mt-6">
                      No vanities match those filters
                    </h3>
                    <p className="mt-3 max-w-md text-charcoal/65 text-sm leading-relaxed">
                      Try removing a filter or two — widening the width or price
                      range usually brings more options back into view.
                    </p>
                    <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                      <button
                        onClick={clearAll}
                        data-testid="empty-clear-all"
                        className="rounded-full bg-charcoal text-ivory px-7 py-3.5 text-sm font-medium btn-fill hover:text-charcoal transition-colors duration-500"
                      >
                        Clear all filters
                      </button>
                      <a
                        href={WHATSAPP_URL("Hi BathLane, I need help finding the right vanity.")}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="empty-whatsapp"
                        className="inline-flex items-center gap-2 rounded-full border border-beige px-6 py-3.5 text-sm font-medium text-charcoal hover:border-charcoal transition-colors duration-300"
                      >
                        <MessageCircle size={16} strokeWidth={1.5} />
                        Get help on WhatsApp
                      </a>
                    </div>
                  </div>

                  {addonItems.length > 0 && (
                    <div className="mt-12">
                      <p className="eyebrow text-taupe mb-4">Taps, wastes & mirrors</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-12">
                        {addonItems.map((p, i) => (
                          <ProductCard key={p.id} product={p} index={i} />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </section>
          </div>
        </div>
      </main>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
